import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { App } from './App';
import { dbAvailable, appId } from '../db/client';
import overlayCss from '../widget-overlay/styles.css?inline';
import widgetCss from './styles.css?inline';

const HOST_ID = 'ui-noter-widget-host';
const ERROR_HOST_ID = 'ui-noter-error-host';
const UNLOCKED_KEY = 'uinoter.unlocked';

let mounted = false;
let reactRoot: Root | null = null;

declare global {
  interface Window {
    uinoter?: {
      show: () => void;
      hide: () => void;
      toggle: () => void;
      status: () => { mounted: boolean; unlocked: boolean; appId: string | null; dbAvailable: boolean };
      version: string;
    };
  }
}

function isUnlockedSticky(): boolean {
  try { return localStorage.getItem(UNLOCKED_KEY) === '1'; } catch { return false; }
}

function setUnlockedSticky(v: boolean) {
  try { v ? localStorage.setItem(UNLOCKED_KEY, '1') : localStorage.removeItem(UNLOCKED_KEY); }
  catch { /* localStorage may be blocked */ }
}

function mount() {
  if (mounted) return;
  if (!dbAvailable) {
    showErrorToast('UI Noter is missing data-app on the <script> tag. Add data-app="<your InstantDB app id>".');
    return;
  }
  if (document.getElementById(HOST_ID)) return;

  try {
    const host = document.createElement('div');
    host.id = HOST_ID;
    host.style.cssText = 'all: initial; position: static;';
    document.documentElement.appendChild(host);

    const shadow = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = `${overlayCss}\n${widgetCss}`;
    shadow.appendChild(style);

    const rootEl = document.createElement('div');
    shadow.appendChild(rootEl);

    reactRoot = createRoot(rootEl);
    reactRoot.render(
      <React.StrictMode>
        <App
          onUnlock={() => setUnlockedSticky(true)}
          onLock={() => setUnlockedSticky(false)}
        />
      </React.StrictMode>,
    );
    mounted = true;
  } catch (err) {
    showErrorToast('UI Noter failed to mount: ' + (err as Error).message);
  }
}

function unmount() {
  try { reactRoot?.unmount(); } catch { /* ignore */ }
  reactRoot = null;
  document.getElementById(HOST_ID)?.remove();
  mounted = false;
}

function showErrorToast(message: string) {
  // Lightweight, no-React toast that works even when the app failed to mount.
  let host = document.getElementById(ERROR_HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = ERROR_HOST_ID;
    host.style.cssText = `
      position: fixed; top: 16px; right: 16px; z-index: 2147483647;
      max-width: 360px; padding: 10px 32px 10px 12px;
      background: #fef2f2; color: #991b1b; border: 1px solid #fecaca;
      border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.10);
      font: 13px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    document.documentElement.appendChild(host);

    const close = document.createElement('button');
    close.textContent = '×';
    close.style.cssText = `
      position: absolute; top: 4px; right: 6px;
      background: transparent; border: none; color: #991b1b;
      font-size: 18px; line-height: 1; cursor: pointer;
    `;
    close.onclick = () => host?.remove();
    host.appendChild(close);

    const body = document.createElement('div');
    host.appendChild(body);
    (host as HTMLElement).dataset.body = 'true';
  }
  const body = host.querySelector('div');
  if (body) body.textContent = message;
}

function applyUrlOverride(): 'open' | 'hide' | 'reset' | null {
  try {
    const v = new URL(location.href).searchParams.get('uinoter');
    if (v === 'open' || v === 'hide' || v === 'reset') return v;
    return null;
  } catch {
    return null;
  }
}

function handleUrlOverride(action: 'open' | 'hide' | 'reset') {
  if (action === 'open') {
    setUnlockedSticky(true);
    mount();
  } else if (action === 'hide') {
    setUnlockedSticky(false);
    unmount();
  } else if (action === 'reset') {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('uinoter.'))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
    // Strip the param and reload so the page resets cleanly.
    const u = new URL(location.href);
    u.searchParams.delete('uinoter');
    location.replace(u.toString());
  }
}

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof Element)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((t as HTMLElement).isContentEditable) return true;
  return false;
}

function onShortcut(e: KeyboardEvent) {
  if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
  const isE = e.code === 'KeyE' || (e.key || '').toLowerCase() === 'e';
  if (!isE) return;
  if (isEditableTarget(e.target)) return;

  e.preventDefault();
  e.stopPropagation();

  if (mounted) {
    setUnlockedSticky(false);
    unmount();
  } else {
    setUnlockedSticky(true);
    mount();
  }
}

function loadFont() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('link[data-uinoter-font]')) return;
  // Loaded at the document level so the font is available inside the shadow DOM.
  const links: Array<[string, string?, string?]> = [
    ['preconnect', 'https://fonts.googleapis.com', undefined],
    ['preconnect', 'https://fonts.gstatic.com', 'anonymous'],
    [
      'stylesheet',
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      undefined,
    ],
  ];
  for (const [rel, href, cors] of links) {
    const l = document.createElement('link');
    l.rel = rel;
    l.href = href!;
    if (cors !== undefined) l.crossOrigin = cors;
    l.dataset.uinoterFont = '1';
    document.head.appendChild(l);
  }
}

function init() {
  if (typeof document === 'undefined') return;
  if (window.top !== window.self) return; // skip in iframes
  loadFont();

  // 1. Boot ping so DevTools shows the bundle ran (even if everything else fails).
  // eslint-disable-next-line no-console
  console.info(
    `[ui-noter] ready · press ⌘⇧E or call window.uinoter.show() · appId=${appId ?? 'NONE'}`,
  );

  // 2. Window.uinoter debug API — escape hatch when the shortcut is captured.
  window.uinoter = {
    show: () => { setUnlockedSticky(true); mount(); },
    hide: () => { setUnlockedSticky(false); unmount(); },
    toggle: () => (mounted ? unmount() : mount()),
    status: () => ({ mounted, unlocked: isUnlockedSticky(), appId, dbAvailable }),
    version: '0.1.0',
  };

  // 3. Surface configuration errors visibly even before any user interaction.
  if (!dbAvailable) {
    showErrorToast(
      'UI Noter: missing or invalid data-app on the <script> tag. Add data-app="<your InstantDB app id>".',
    );
  }

  // 4. Honour ?uinoter=open|hide|reset URL param.
  const action = applyUrlOverride();
  if (action) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => handleUrlOverride(action), { once: true });
    } else {
      handleUrlOverride(action);
    }
  } else if (isUnlockedSticky()) {
    // 5. Or auto-mount if previously unlocked on this origin.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mount, { once: true });
    } else {
      mount();
    }
  }

  // 6. Hardened keydown listener: both targets, capture phase, layout-tolerant.
  document.addEventListener('keydown', onShortcut, true);
  window.addEventListener('keydown', onShortcut, true);

  // 7. App's "hide" button (toolbar ×) tells us to unmount.
  window.addEventListener('uinoter:hide', () => {
    setUnlockedSticky(false);
    unmount();
  });
}

init();
